/**
 * Data hooks for the Operations Surface (ADR-0119).
 *
 *   useSurface() — polls GET /api/surface every 15s (matches the books).
 *   usePins()    — the same cache entry, read-only and unpolled, for surfaces
 *                  outside the Operations Surface that need pin state (#541:
 *                  the case-detail panel's pin toggle).
 *
 * Returns the payload's two parts: the rule-surfaced `belt` (BeltBlocks of
 * DashboardRow rows) and the operator's private `pinned` watches; the page
 * derives its By-firm tab from the belt. Bespoke `useQuery` rather than
 * `createPollingQuery` because the response embeds `DashboardRowSchema`, whose
 * `.default()` fields make the factory's `ZodType<T>` collapse input/output and
 * type the rows loosely; the explicit `useQuery<SurfaceResponse>` keeps the
 * output type (so a belt row is a full DashboardRow). Writes (pin via
 * useCreateTask, un-pin via useTaskAction `clear`) all
 * invalidate ["surface"] so the tabs reflect a change on the next render.
 */

import { useQuery } from "@tanstack/react-query";
import {
  SurfaceResponseSchema,
  type SurfacePin,
  type SurfaceResponse,
} from "@command-center/shared";
import { apiGet } from "./api";

async function fetchSurface(): Promise<SurfaceResponse> {
  const raw = await apiGet<unknown>("/api/surface");
  const parsed = SurfaceResponseSchema.safeParse(raw);
  if (!parsed.success) {
    throw new Error(
      `/api/surface failed schema validation: ${parsed.error.message}`,
    );
  }
  return parsed.data;
}

export function useSurface() {
  return useQuery<SurfaceResponse, Error>({
    queryKey: ["surface"],
    queryFn: fetchSurface,
    refetchInterval: 15_000,
  });
}

/**
 * The viewer's pins, for pin toggles outside the Operations Surface (#541 —
 * the case-detail panel). Shares the ["surface"] cache entry with useSurface
 * (so pin/un-pin invalidations keep every consumer coherent) but registers no
 * poll of its own — a panel needs a point-in-time read, not a 15s cadence.
 * `enabled: false` skips the fetch entirely (a panel with no lead target).
 * While the fetch is in flight or failed, `pins` is [] — the toggle just
 * renders unpinned, which is the safe read.
 *
 * Cost note (accepted at the current two-operator scale): /api/surface builds
 * the full belt + pinned card map server-side, so a cold-cache panel open does
 * real work to answer one boolean. The reads ride the same KV + 30s-staleTime
 * caching as everything else. If panel-open latency ever matters, the upgrade
 * path is a lightweight pins-only read (the viewer's live watch tasks) instead
 * of the full surface.
 */
export function usePins(enabled = true): { pins: SurfacePin[] } {
  const query = useQuery<SurfaceResponse, Error>({
    queryKey: ["surface"],
    queryFn: fetchSurface,
    enabled,
  });
  return { pins: query.data?.pinned ?? [] };
}
